export interface AdminChangeSubscriptionParams_Base {
  userId: string;
  // Optional since #3949: the `ChangeNextPaymentDate` (Grant Free Days) action
  // is now provider-agnostic and the backend derives the subscription id from
  // the user record, so the client no longer passes one. `Synchronize` joined it
  // in #4248 Phase 3, when it was widened to re-sync all four providers — no
  // caller passes an id for it any more and the server never reads one. The
  // remaining PayPro-only actions (ChangeProduct, Cleanup, Terminate) still
  // pass it.
  subscriptionId?: number;
  action: string;
  // Optional since #3949: the Grant Free Days action no longer asks the client
  // for this — the backend never notifies the customer for a make-good. The
  // PayPro-only actions still pass it; their handlers coerce a missing value to
  // `false` (PayPro's "no email" default).
  sendCustomerNotification?: boolean;
};

export interface AdminChangeSubscriptionResult_Base {
  error?: string; // empty if all is ok
  /**
   * True when the provider accepted the change, our own save then failed, and
   * the server repaired that by itself — re-reading the provider and saving what
   * it said. The action really did succeed, so `error` stays empty; this flag is
   * what stops the audit row claiming the first save simply worked.
   *
   * The client does not render it. It exists so a run of these is findable in
   * `log_subscription_transactions`, which is where "how often does the write
   * path lose a save?" has to be answerable from.
   */
  halfStateRepaired?: boolean;
};

// compare the data in our internal user database with the data in the external payment processor's database and update the former to match the latte
export interface AdminChangeSubscriptionParams_Synchronize extends AdminChangeSubscriptionParams_Base {
  action: 'Synchronize';
};

export interface AdminChangeSubscriptionResult_Synchronize extends AdminChangeSubscriptionResult_Base {
  updated: boolean; // whether any update was needed
  /**
   * Present when this Re-sync ALSO re-read the coach's Paddle coaching seat,
   * beside the subscription the licence type names (#5958). `updated` above
   * covers both reads; this says what the seat read found on its own, and
   * `error` is set when that read failed. Absent when the account has no seat,
   * when the seat IS the licence subscription, and from servers older than
   * #5958, which never read the seat in this case.
   */
  coachSeatReread?: { updated: boolean; error?: string };
};

export interface AdminChangeSubscriptionParams_ChangeProduct extends AdminChangeSubscriptionParams_Base {
  action: 'ChangeProduct';
  newProductId: number;
};

// use this for subscriptions that are not going to be renewed (Terminated or Finished) - when we are updating all subscriptions to a new product
export interface AdminChangeSubscriptionParams_Cleanup extends AdminChangeSubscriptionParams_Base {
  action: 'Cleanup';
};

export interface AdminChangeSubscriptionParams_Terminate extends AdminChangeSubscriptionParams_Base {
  action: 'Terminate';
};

// Cancel a subscription at the END of the current billing period, keeping the
// customer's access until then — the default support cancel action (#4248
// Phase 2). Distinct from the immediate, PayPro-only `Terminate`: this is the
// promise-keeping cancel we made to customers on #4172 / #4177 / #4246. Applies
// to the web rails (Paddle + PayPro); Apple / Google Play are refused by the
// capability matrix with a message telling the customer to cancel in the store.
// Provider-agnostic like Grant Free Days: the client sends only the action + the
// user id, and the backend dispatches on `userInfo.licenseType`.
export interface AdminChangeSubscriptionParams_CancelAtPeriodEnd extends AdminChangeSubscriptionParams_Base {
  action: 'CancelAtPeriodEnd';
};

// Undo a pending cancellation — the mirror image of `CancelAtPeriodEnd`
// (#4248 Phase 4). For Paddle it un-schedules the pending change (or un-pauses a
// subscription whose pause already took effect); for PayPro it renews a
// suspended subscription. Apple / Google Play are refused by the capability
// matrix: only the customer can re-subscribe in their own store.
// Provider-agnostic like the other newer actions — the client sends the action
// and the user id, and the backend dispatches on `userInfo.licenseType`.
export interface AdminChangeSubscriptionParams_Resume extends AdminChangeSubscriptionParams_Base {
  action: 'Resume';
};

// "Grant Free Days" make-good. Provider-agnostic since #3949: the client sends
// only the day count; the backend reads `userInfo.licenseType`, picks the right
// provider branch (PayPro / Google Play / Apple / Paddle), and computes the
// target date / duration itself. See design-docs/admin-grant-free-days.md.
export interface AdminChangeSubscriptionParams_ChangeNextPaymentDate extends AdminChangeSubscriptionParams_Base {
  action: 'ChangeNextPaymentDate';
  daysToGrant: number; // how many free days to add to the current next-charge / expiry date
};

// Move a subscription from the wrong account to the right one (#4248 Phase 6) —
// the duplicate-account case (#4227, #4198, #4207). `userId` is the account the
// subscription is moving FROM; `toUserId` is where it lands. This is the only
// IRREVERSIBLE action in the tooling, so it is deliberately script-only: there
// is no Admin UI button, and the CLI dry-runs by default. It refuses rather than
// half-applies — see `planSubscriptionRepoint` for the rules.
export interface AdminChangeSubscriptionParams_RepointSubscription extends AdminChangeSubscriptionParams_Base {
  action: 'RepointSubscription';
  /** The account the subscription moves TO. */
  toUserId: string;
};

// Switch a customer between the monthly and yearly plan (#4248 Phase 7) — the
// Paddle counterpart of the PayPro-only `ChangeProduct`. Deliberately carries NO
// price id: the server derives the one legitimate target from the env's
// configured monthly/yearly pair, so support cannot move a customer onto the
// coach-seat price or a legacy price, by typo or otherwise. Provider-agnostic in
// shape like the other newer actions — the client sends the action and the user
// id — though only Paddle currently supports it; the capability matrix refuses
// the rest by name.
export interface AdminChangeSubscriptionParams_SwitchPlan extends AdminChangeSubscriptionParams_Base {
  action: 'SwitchPlan';
};

// Cancel a TEST coach-seat subscription at Paddle (#4755) — the per-seat COACHING
// subscription in `coachSeatData`, NOT the personal `paddleData` one every other
// action here reads. A coach can hold both at once.
//
// DELIBERATELY TEST-ONLY, and the name says so rather than hiding it: both the
// capability matrix and the handler refuse unless `coachSeatData.isTestPurchase`
// is true and the Paddle environment is the sandbox. Cancelling a REAL coach's
// seat also ends their clients' Premium through the coach licence mirror, so it
// belongs in its own ticket. See `design-docs/subscription-ops-tooling.md`.
export interface AdminChangeSubscriptionParams_CancelTestCoachSeat extends AdminChangeSubscriptionParams_Base {
  action: 'CancelTestCoachSeat';
};

// Give a customer back the price PayPro charged them, after we moved them onto
// Paddle and they found it costs more (#5084). Works by attaching a flat,
// recurring Paddle discount restricted to the price they bill on, so their next
// charge lands on the old amount.
//
// Deliberately carries NO discount id and NO discount amount: the server reads
// the customer's live Paddle quote and derives the difference itself, so a typo
// cannot turn into a permanent discount. `targetMinor` is the one thing an
// officer may supply, and it is a TARGET PRICE rather than a discount — a
// wrong one is caught by the server's share guard instead of being applied.
// It is needed only for a currency the server has no measured PayPro price for;
// leave it unset for the eurozone, which is the covered case.
//
// Does not decide whether the customer deserves the grant. That is the
// officer's judgement (owner decision 2026-09-08) and the audit row records who
// made it — see design-docs/paddle-integration.md.
export interface AdminChangeSubscriptionParams_GrantLegacyPrice extends AdminChangeSubscriptionParams_Base {
  action: 'GrantLegacyPrice';
  /** Report what the grant WOULD do without writing anything at Paddle. */
  dryRun?: boolean;
  /** What the customer should end up paying, in the currency's smallest unit.
   *  Omit to use the server's measured figure for their currency. */
  targetMinor?: number;
};

// Hand an Apple subscription to the account the customer is signed in to now,
// when the purchase was made from one of our accounts that no longer exists
// (#5384). Apple signs which of our accounts started a purchase, and the server
// refuses a purchase that names a different account. So a customer who deleted
// their account and made a new one cannot restore their own subscription, and
// store subscriptions cannot be repointed. This asks Apple to replace the
// account it holds on the subscription with this account's, then grants it.
//
// The officer supplies the transaction id because nothing on our side can
// derive it: the account asking has never held it. Support takes it from the
// customer's receipt or from the `store.account_token.mismatch` report
// their refused restore wrote. Whether the person asking really paid for it is
// the officer's judgement, and the audit row records who made it.
//
// Refused when another EXISTING account holds the subscription — that account
// is the owner, and the customer should sign in to it. Script-only, like the
// repoint: there is no Admin UI button.
export interface AdminChangeSubscriptionParams_RebindAppleAccount extends AdminChangeSubscriptionParams_Base {
  action: 'RebindAppleAccount';
  /** Apple's id for the subscription line, from the customer's receipt or from
   *  the refused restore's log line. */
  originalTransactionId: string;
  /** Report what would happen without changing anything at Apple or here. */
  dryRun?: boolean;
};

/** Which of our accounts Apple currently holds on a subscription, as far as this
 *  account can tell. The server cannot name another account: Apple only holds
 *  a one-way hash of it. */
export enum AppleAccountOnRecord {
  /** Apple names no account on it. */
  NoAccount = 'NoAccount',
  /** Apple already names this account. */
  ThisAccount = 'ThisAccount',
  /** Apple names a different one of our accounts. */
  AnotherAccount = 'AnotherAccount',
}

/** What a re-bind found at Apple and, unless it was a dry run, did (#5384). */
export interface AdminAppleRebindSummary {
  /** Which account Apple held BEFORE this action. */
  accountBefore: AppleAccountOnRecord;
  /** Apple's own word for the subscription's state, e.g. `active`. */
  appleStatus: string;
  /** `Sandbox` or `Production`, as Apple reported it. */
  appleEnvironment?: string;
  /** True when nothing was changed at Apple or on our side. */
  dryRun: boolean;
}

export type AdminChangeSubscriptionParams =
  AdminChangeSubscriptionParams_Synchronize |
  AdminChangeSubscriptionParams_ChangeProduct |
  AdminChangeSubscriptionParams_Cleanup |
  AdminChangeSubscriptionParams_ChangeNextPaymentDate |
  AdminChangeSubscriptionParams_Terminate |
  AdminChangeSubscriptionParams_CancelAtPeriodEnd |
  AdminChangeSubscriptionParams_Resume |
  AdminChangeSubscriptionParams_SwitchPlan |
  AdminChangeSubscriptionParams_GrantLegacyPrice |
  AdminChangeSubscriptionParams_CancelTestCoachSeat |
  AdminChangeSubscriptionParams_RebindAppleAccount |
  AdminChangeSubscriptionParams_RepointSubscription;

/** What a legacy-price grant worked out and (unless it was a dry run) applied
 *  (#5084). Amounts are in the currency's smallest unit, as Paddle sends them,
 *  so the caller formats them for its own audience rather than receiving a
 *  pre-formatted string that only suits one of the two front-ends. */
export interface AdminLegacyPriceGrantSummary {
  currencyCode: string;
  /** What Paddle quotes for the next charge today, inclusive of tax. */
  quotedMinor: number;
  /** What the customer pays after the grant. */
  targetMinor: number;
  /** The difference — what was sent to Paddle as the discount amount. */
  discountMinor: number;
  /** 1 or 12 — which billing cycle the target was taken for. */
  months: number;
  /** True when the target came from the officer rather than from the server's
   *  own measured figure for this currency. */
  targetWasSupplied: boolean;
  /** True when nothing was written at Paddle. */
  dryRun: boolean;
  /** The discount now attached. Absent on a dry run. */
  discountId?: string;
  /** Set when a discount object was CREATED rather than re-used. Named so an
   *  operator can find one left behind by a failed attach. */
  createdDiscountId?: string;
}

/**
 * Render a minor-unit amount for a person to read: `formatMinorAmount('EUR', 1999)`
 * gives `EUR 19.99` (#5084).
 *
 * Shared because all three front-ends of the legacy-price grant quote the same
 * figures — the server's refusal messages, the Admin UI confirmation, and the
 * CLI dry run — and three copies of one formatter is three places to fix the
 * limitation below.
 *
 * **It assumes the currency has 2 decimal places.** Every currency the grant
 * actually covers does (the EUR group), and the amounts are ALSO reported
 * unformatted in {@link AdminLegacyPriceGrantSummary} precisely so a caller is
 * never stuck with this rendering. The zero-decimal currencies on Paddle's
 * ladder — JPY, KRW, CLP, VND — would read 100x too small here; they can only
 * be reached by an officer supplying an explicit target for a currency the
 * server has no measured PayPro price for, and that is the point at which to
 * teach this function about decimal places rather than guess now.
 */
export function formatMinorAmount(currencyCode: string, minor: number): string {
  return `${currencyCode} ${(minor / 100).toFixed(2)}`;
}

export interface AdminChangeSubscriptionResult_GrantLegacyPrice extends AdminChangeSubscriptionResult_Base {
  /** Present whenever the server got far enough to work a grant out — including
   *  alongside an `error`, so a failure message can say what was attempted. */
  grant?: AdminLegacyPriceGrantSummary;
};

export interface AdminChangeSubscriptionResult_RebindAppleAccount extends AdminChangeSubscriptionResult_Base {
  /** Present whenever the server got as far as reading the subscription from
   *  Apple — including alongside an `error`, so a refusal can say what it saw. */
  rebind?: AdminAppleRebindSummary;
};

export type AdminChangeSubscriptionResult =
  AdminChangeSubscriptionResult_Base |
  AdminChangeSubscriptionResult_Synchronize |
  AdminChangeSubscriptionResult_GrantLegacyPrice |
  AdminChangeSubscriptionResult_RebindAppleAccount;
